export const SP = {
  lime: '#C2E530',
  limeGlow: '#D4FF3A',
  charcoal: '#353535',
  charcoalDeep: '#1A1A1A',
  bg: '#0C0C0C',
  bgRaised: '#161616',
  card: '#181818',
  cardRaised: '#202020',
  border: 'rgba(255,255,255,0.08)',
  borderBright: 'rgba(255,255,255,0.16)',
  text: '#F4F2F2',
  muted: 'rgba(244,242,242,0.6)',
  dim: 'rgba(244,242,242,0.35)',
  mono: "'JetBrains Mono', ui-monospace, monospace",
  body: "'Lora', Georgia, serif",
  ui: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
} as const

export const APP_STORE_URL = 'https://apps.apple.com/us/app/stazon/id6771209060'
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.stazon.StazonApp'
export const ANDROID_PACKAGE = 'com.stazon.StazonApp'

export function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

export function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent)
}

// ON-455 / ON-411: the app tags share links
// (`/share/event/<id>?lang=en&ref=<uuid>&src=<surface>`, `open=chat`, ...).
// The whole query string has to ride along on every hand-off to the app, or
// the app can never act on or report those tags. Values are not validated
// here — the app owns that.

/**
 * Re-serialises a search string (`location.search`, with or without the
 * leading `?`) for use on an app hand-off link. Returns it including the
 * leading `?`, or '' when there is nothing to forward. Never throws.
 *
 * Rebuilt through URLSearchParams rather than concatenated raw, so every key
 * and value is percent-encoded: a `#`, `;` or `&` inside a value cannot end
 * the query early or inject into the Android `#Intent;...;end` fragment.
 */
export function handoffQuery(search: string | null | undefined): string {
  if (!search) return ''
  try {
    // Drop anything from a raw '#' on: that is a fragment, not query.
    const raw = (search.startsWith('?') ? search.slice(1) : search).split('#')[0]
    // %20 rather than '+' for spaces — unambiguous for any parser on the app
    // side (a literal '+' is already encoded as %2B).
    const qs = new URLSearchParams(raw).toString().replace(/\+/g, '%20')
    return qs ? `?${qs}` : ''
  } catch {
    return ''
  }
}

export function deeplinkFor(type: 'event' | 'group', id: string, search?: string | null): string {
  return `stazon://share/${type}/${id}${handoffQuery(search)}`
}

/**
 * Android `intent://` URL for the https share link. Chrome hands the app the
 * https URL rebuilt from host + path + query, so the query must sit before the
 * `#Intent` fragment.
 */
export function androidIntentUrl(host: string, pathname: string, search?: string | null): string {
  const fallback = encodeURIComponent(PLAY_STORE_URL)
  return (
    `intent://${host}${pathname}${handoffQuery(search)}` +
    `#Intent;scheme=https;package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${fallback};end`
  )
}

let _deepLinkTimer: ReturnType<typeof setTimeout> | null = null

export function openDeepLink(url: string) {
  if (_deepLinkTimer !== null) {
    clearTimeout(_deepLinkTimer)
    _deepLinkTimer = null
  }
  window.location.assign(url)
  // If the native app opens, the page loses focus — cancel the store redirect
  const cancel = () => {
    if (_deepLinkTimer !== null) {
      clearTimeout(_deepLinkTimer)
      _deepLinkTimer = null
    }
    window.removeEventListener('blur', cancel)
  }
  window.addEventListener('blur', cancel)
  _deepLinkTimer = setTimeout(() => {
    window.removeEventListener('blur', cancel)
    if (isIOS()) {
      window.location.assign(APP_STORE_URL)
    } else if (isAndroid()) {
      window.location.assign(PLAY_STORE_URL)
    }
    _deepLinkTimer = null
  }, 1500)
}

export function openInApp(type: 'event' | 'group', id: string, search?: string | null) {
  openDeepLink(deeplinkFor(type, id, search))
}

// Soft attempt — try to open the app without bouncing to a store on failure.
// Used for the once-per-session auto-redirect on page load.
export function attemptOpenApp(type: 'event' | 'group', id: string, search?: string | null) {
  const key = `stazon:auto-open:${type}:${id}`
  if (sessionStorage.getItem(key)) return
  sessionStorage.setItem(key, '1')

  if (isAndroid()) {
    // Android Chrome respects intent:// URLs and falls back cleanly to Play Store
    // if the app is not installed.
    window.location.assign(androidIntentUrl(window.location.host, window.location.pathname, search))
  }
  // iOS: do nothing. Universal Links must fire from the original tap of the
  // https URL — by the time JS runs that ship has sailed, and a JS-triggered
  // stazon:// jump shows an "Cannot Open Page" alert when the app is missing.
  // Smart App Banner + the Open button cover iOS.
}
